<script setup lang="ts">
import { reactive, ref } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import router from "../../router";
import axios from "axios";
import { ElMessage } from "element-plus";
import { useAuthStore, type userDataInterface } from "../../store/authStore";
import { isCompanyAdminUserType } from "../../utils/authRole";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

interface RuleForm {
  name: string;
  email: string;
  phone_no: string;
}

const formError = ref("");
const results = ref();
const loading = ref(false);
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive<RuleForm>({ name: "", phone_no: "", email: "" });

const rules = reactive<FormRules<RuleForm>>({
  name: [
    { required: true, message: "Please enter your name", trigger: "blur" },
  ],
  phone_no: [
    {
      required: true,
      message: "Please enter your phone number",
      trigger: "blur",
    },
  ],
  email: [
    {
      required: true,
      message: "Please enter your email address",
      trigger: "blur",
    },
  ],
});

const initializeForm = () => {
  const u = store.getUserData as userDataInterface;
  ruleForm.email = u.user_email || u.email || "";
  ruleForm.phone_no = u.user_phone;
  ruleForm.name = u.user_name;
};
initializeForm();

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate((valid) => {
    if (valid) {
      loading.value = true;
      postData();
    }
  });
};

const postData = async () => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_ONBOARDING_SERVICE}updateuser?token=${store.getToken}`,
      {
        email: ruleForm.email,
        name: ruleForm.name,
        phone: ruleForm.phone_no,
        company_id: store.getUserData.company_id,
        user_id: store.getUserData.user_id,
        user_type: store.getUserData.user_type,
      },
    );
    loading.value = false;
    if (response.status == 200 && response.data.status) {
      ElMessage({ message: "Profile updated successfully", type: "success" });
      updateStore(ruleForm.name, ruleForm.email, ruleForm.phone_no);
      router.push(
        isCompanyAdminUserType(store.getUserData.user_type)
          ? "/users"
          : "/request",
      );
    } else {
      ElMessage({ message: "Update failed, please retry.", type: "error" });
    }
  } catch {
    loading.value = false;
    formError.value = "Something went wrong, please try again.";
  }
};

const updateStore = (name: string, email: string, phone: string) => {
  store.setUserData({
    ...store.getUserData,
    email,
    user_email: email,
    user_name: name,
    user_phone: phone,
  });
};

const logwat = () => {
  store.setAuthStatus(false);
  store.$reset();
  localStorage.removeItem("_panda");
  router.push("/login");
};
</script>

<template>
  <div class="fp-page">
    <div class="fp-inner">
      <div class="fp-card" v-loading="loading">
        <div class="fp-card-header">
          <div class="fp-card-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2563eb"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div>
            <h1 class="fp-title">My Profile</h1>
            <p class="fp-subtitle">Update your personal information</p>
          </div>
        </div>

        <!-- Avatar -->
        <div class="fp-avatar-row">
          <div class="fp-avatar">
            {{ ruleForm.name?.charAt(0)?.toUpperCase() || "?" }}
          </div>
          <div>
            <div class="fp-avatar-name">{{ ruleForm.name || "Your Name" }}</div>
            <div class="fp-avatar-email">
              {{ ruleForm.email || "your@email.com" }}
            </div>
          </div>
        </div>

        <div class="fp-divider"></div>
        <div class="fp-error" v-if="formError">{{ formError }}</div>

        <el-form
          ref="ruleFormRef"
          :model="ruleForm"
          :rules="rules"
          label-width="0px"
          status-icon
        >
          <div class="fp-field">
            <label class="fp-label"
              >Full Name <span class="fp-required">*</span></label
            >
            <el-form-item prop="name" class="fp-form-item">
              <input
                v-model="ruleForm.name"
                class="fp-input"
                placeholder="Your full name"
              />
            </el-form-item>
          </div>

          <div class="fp-field">
            <label class="fp-label"
              >Email Address <span class="fp-required">*</span></label
            >
            <el-form-item prop="email" class="fp-form-item">
              <input
                v-model="ruleForm.email"
                class="fp-input"
                type="email"
                placeholder="your@email.com"
              />
            </el-form-item>
          </div>

          <div class="fp-field">
            <label class="fp-label"
              >Phone Number <span class="fp-required">*</span></label
            >
            <el-form-item prop="phone_no" class="fp-form-item">
              <MazPhoneNumberInput
                v-model="ruleForm.phone_no"
                defaultCountryCode="KE"
                :only-countries="['KE', 'UG', 'TZ', 'NG', 'ZA']"
                @update="results = $event"
                :success="results?.isValid"
                class="fp-maz"
              />
            </el-form-item>
          </div>

          <button
            class="fp-submit-btn"
            @click.prevent="submitForm(ruleFormRef)"
            :disabled="loading"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Save Changes
          </button>
        </el-form>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap");
* {
  box-sizing: border-box;
}
.fp-page {
  font-family: "DM Sans", sans-serif;
  background: #f8fafc;
  min-height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 24px;
}
.fp-inner {
  width: 100%;
  max-width: 480px;
}
.fp-card {
  background: white;
  border-radius: 16px;
  border: 1.5px solid #e2e8f0;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(18, 40, 74, 0.07);
}
.fp-card-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 22px;
}
.fp-card-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.fp-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #12284a;
  margin: 0 0 3px;
}
.fp-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
  margin: 0;
}
.fp-avatar-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
}
.fp-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(135deg, #12284a 0%, #1e3f73 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: 700;
}
.fp-avatar-name {
  font-size: 0.92rem;
  font-weight: 600;
  color: #12284a;
}
.fp-avatar-email {
  font-size: 0.78rem;
  color: #94a3b8;
  margin-top: 2px;
}
.fp-divider {
  height: 1px;
  background: #f1f5f9;
  margin: 0 -32px 22px;
}
.fp-error {
  background: #fef2f2;
  border: 1.5px solid #fca5a5;
  color: #dc2626;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.82rem;
  font-weight: 500;
  margin-bottom: 20px;
}
.fp-field {
  margin-bottom: 16px;
}
.fp-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
}
.fp-required {
  color: #dc2626;
}
.fp-form-item {
  margin-bottom: 0 !important;
}
:deep(.fp-form-item .el-form-item__content) {
  display: block;
}
:deep(.fp-form-item .el-form-item__error) {
  padding-top: 4px;
  font-size: 0.74rem;
}
.fp-input {
  width: 100%;
  height: 42px;
  padding: 0 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.87rem;
  color: #1e293b;
  outline: none;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  background: white;
}
.fp-input:focus {
  border-color: #12284a;
  box-shadow: 0 0 0 3px rgba(18, 40, 74, 0.08);
}
.fp-input::placeholder {
  color: #94a3b8;
}
.fp-maz {
  width: 100%;
}
:deep(.fp-maz .m-phone-number-input) {
  width: 100%;
}
.fp-submit-btn {
  width: 100%;
  height: 44px;
  margin-top: 10px;
  background: linear-gradient(135deg, #12284a 0%, #1e3f73 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.15s;
}
.fp-submit-btn:hover:not(:disabled) {
  opacity: 0.9;
}
.fp-submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
